import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl, formatIDR, formatDateTime, formatApiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Receipt, Truck, CheckCircle2, CreditCard, MessageSquare, Send, AlertCircle, XCircle, AlertTriangle, Star } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import VerifiedBadge from "@/components/VerifiedBadge";
import StarRating from "@/components/StarRating";

const STATUS_STYLES = {
  pending_payment: { bg: "#F4F4F5", text: "#71717A", label: "MENUNGGU PEMBAYARAN" },
  paid: { bg: "#E0E7FF", text: "#3730A3", label: "DIBAYAR — DANA DI ESCROW" },
  purchased: { bg: "#FEF3C7", text: "#92400E", label: "DIBELI" },
  shipping: { bg: "#F3E8FF", text: "#6B21A8", label: "DIKIRIM" },
  delivered: { bg: "#DCFCE7", text: "#166534", label: "SAMPAI" },
  released: { bg: "#F1F5F9", text: "#334155", label: "DANA DILEPAS" },
  cancelled: { bg: "#FEE2E2", text: "#991B1B", label: "DIBATALKAN" },
  refunded: { bg: "#FEE2E2", text: "#991B1B", label: "DIREFUND" },
  disputed: { bg: "#FECACA", text: "#7F1D1D", label: "SENGKETA" },
};

const TIMELINE_STEPS = [
  { key: "created", label: "Dibuat", icon: CheckCircle2 },
  { key: "paid", label: "Dibayar ke Escrow", icon: CreditCard },
  { key: "purchased", label: "Dibeli", icon: Receipt },
  { key: "shipping", label: "Dikirim", icon: Truck },
  { key: "delivered", label: "Sampai", icon: CheckCircle2 },
  { key: "released", label: "Dana Dilepas", icon: ShieldCheck },
];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceiptForm, setShowReceiptForm] = useState(false);
  const [receiptPath, setReceiptPath] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");

  const reload = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data));

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-20" style={{color: "var(--app-muted)"}}>Memuat...</div>;
  if (!order) return <div className="text-center py-20" style={{color: "var(--app-muted)"}}>Order tidak ditemukan</div>;

  const isBuyer = user?.id === order.buyer_id;
  const isJastiper = user?.id === order.jastiper_id;
  const s = STATUS_STYLES[order.escrow_status] || STATUS_STYLES.pending_payment;
  const isDisputed = order.escrow_status === "disputed";
  const isFinalized = ["released", "cancelled", "refunded"].includes(order.escrow_status);

  const completedKeys = new Set((order.timeline || []).map((t) => t.status));

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      if (action === "pay") {
        await api.post(`/orders/${id}/pay`);
        toast.success("Pembayaran diterima! Dana ditahan di Escrow.");
      } else if (action === "upload-receipt") {
        if (!receiptPath) {
          toast.error("Mohon unggah bukti pembelian");
          setActionLoading(false);
          return;
        }
        await api.post(`/orders/${id}/upload-receipt`, { proof_path: receiptPath, keterangan: trackingNumber });
        toast.success("Bukti pembelian terunggah!");
        setShowReceiptForm(false);
      } else if (action === "mark-shipping") {
        await api.post(`/orders/${id}/mark-shipping`, { tracking_number: trackingNumber || order.tracking_number || "N/A" });
        toast.success("Ditandai sedang dikirim");
      } else if (action === "confirm") {
        if (!window.confirm("Konfirmasi barang sampai? Dana akan dilepas ke Jastiper.")) return;
        await api.post(`/orders/${id}/confirm-arrival`);
        toast.success("Dana dilepas!");
      } else if (action === "cancel") {
        if (!window.confirm("Batalkan order ini?")) return;
        await api.post(`/orders/${id}/cancel`);
        toast.success("Order dibatalkan");
      }
      await reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Aksi gagal");
    } finally {
      setActionLoading(false);
    }
  };

  const reportIssue = async () => {
    if (!disputeReason.trim() || disputeReason.trim().length < 10) {
      toast.error("Mohon jelaskan masalahnya (min. 10 karakter)");
      return;
    }
    if (!window.confirm("Laporkan masalah ini? Dana akan ditahan platform sampai admin meninjau.")) return;
    setActionLoading(true);
    try {
      await api.post(`/orders/${id}/report-issue`, { reason: disputeReason.trim() });
      toast.success("Masalah dilaporkan. Tim admin akan meninjau.");
      setShowDisputeForm(false);
      setDisputeReason("");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Gagal melaporkan");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="order-detail-page">
      <Link to="/dashboard" className="text-sm text-zinc-500 hover:text-[#002FA7] flex items-center gap-2" data-testid="back-to-dashboard">
        <ArrowLeft size={14} strokeWidth={1.5} /> Dashboard
      </Link>

      <div>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="label-caps px-3 py-1.5 font-mono text-xs" style={{ backgroundColor: s.bg, color: s.text }} data-testid="order-status-badge">
            {s.label}
          </span>
          <span className="font-mono text-xs text-zinc-500">ORDER #{order.id.slice(0, 8).toUpperCase()}</span>
          {order.mock_transaction_id && <span className="font-mono text-xs text-zinc-500">TX {order.mock_transaction_id}</span>}
        </div>
        <h1 className="font-display text-3xl tracking-tight">
          {order.items?.[0]?.name}{(order.items?.length || 0) > 1 && <span className="text-zinc-500"> + {order.items.length - 1} more</span>}
        </h1>
        <div className="text-zinc-600 mt-2 text-sm">
          From {order.country} · Created {formatDateTime(order.created_at)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Escrow timeline */}
          <div className="border border-zinc-200 bg-white p-6">
            <div className="label-caps text-zinc-500 mb-5">Escrow Timeline</div>
            <div className="space-y-0 stagger-fade">
              {TIMELINE_STEPS.map((step, idx) => {
                const done = completedKeys.has(step.key) || (step.key === "created" && true);
                const entry = (order.timeline || []).find((t) => t.status === step.key);
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex gap-4 relative" data-testid={`timeline-${step.key}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 flex items-center justify-center border ${done ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200 text-zinc-300"}`}>
                        <Icon size={16} strokeWidth={1.5} />
                      </div>
                      {idx < TIMELINE_STEPS.length - 1 && <div className={`flex-1 w-px ${done ? "bg-[#002FA7]" : "bg-zinc-200"} min-h-[28px]`} />}
                    </div>
                    <div className={`flex-1 pb-6 ${done ? "" : "opacity-40"}`}>
                      <div className={`font-medium ${done ? "text-zinc-900" : "text-zinc-400"}`}>{step.label}</div>
                      {entry && <div className="text-xs text-zinc-500 mt-0.5">{formatDateTime(entry.at)} · {entry.note}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Items */}
          <div className="border border-zinc-200 bg-white p-6">
            <div className="label-caps text-zinc-500 mb-4">Items</div>
            <div className="space-y-3">
              {order.items?.map((it, idx) => (
                <div key={idx} className="flex gap-4 items-center border-b border-zinc-100 pb-3 last:border-b-0 last:pb-0">
                  <div className="w-16 h-16 bg-zinc-100 flex-shrink-0">
                    {it.photo_path && <img src={fileUrl(it.photo_path)} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{it.name}</div>
                    {it.description && <div className="text-xs text-zinc-500 line-clamp-2">{it.description}</div>}
                    <div className="text-xs text-zinc-500 mt-1">Qty: {it.qty || 1}</div>
                  </div>
                  <div className="font-mono text-sm">{formatIDR(it.price * (it.qty || 1))}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Bukti Pembelian */}
          {(order.receipt_path || order.proof_path) && (
            <div className="surface p-6" data-testid="proof-section">
              <div className="label-caps mb-4 flex items-center gap-2"><Receipt size={14} strokeWidth={1.5} /> Bukti Pembelian</div>
              <img src={fileUrl(order.proof_path || order.receipt_path)} alt="Bukti Pembelian" className="max-h-96 mx-auto border" style={{borderColor: "var(--app-border)"}} data-testid="receipt-image" />
              {(order.keterangan || order.tracking_number) && (
                <div className="mt-3 text-sm font-mono">Keterangan: <strong>{order.keterangan || order.tracking_number}</strong></div>
              )}
              {order.status_note && (
                <div className="mt-1 text-sm">Status: <strong>{order.status_note}</strong></div>
              )}
            </div>
          )}

          {/* Chat */}
          <Chat orderId={order.id} />
        </div>

        <div className="space-y-4">
          {/* Dispute banner */}
          {isDisputed && (
            <div className="border-2 p-4 space-y-2" style={{borderColor: "#DC2626", backgroundColor: "#FEE2E2"}} data-testid="dispute-banner">
              <div className="flex items-center gap-2 font-semibold" style={{color: "#7F1D1D"}}>
                <AlertTriangle size={16} strokeWidth={2} /> Sengketa Aktif
              </div>
              <p className="text-sm" style={{color: "#7F1D1D"}}>
                Dilaporkan oleh <strong>{order.dispute_reporter_name}</strong>. Dana ditahan platform sampai admin menyelesaikan.
              </p>
              {order.dispute_reason && (
                <p className="text-xs italic" style={{color: "#7F1D1D"}}>
                  "{order.dispute_reason}"
                </p>
              )}
            </div>
          )}

          {/* Price breakdown */}
          <div className="surface p-5" data-testid="order-price-breakdown">
            <div className="label-caps mb-3">Rincian Pembayaran</div>
            <div className="font-mono text-sm space-y-1.5">
              <div className="flex justify-between"><span style={{color: "var(--app-muted)"}}>Items</span><span>{formatIDR(order.item_total)}</span></div>
              {(order.shipping_fee || 0) > 0 && (
                <div className="flex justify-between"><span style={{color: "var(--app-muted)"}}>Shipping</span><span>{formatIDR(order.shipping_fee)}</span></div>
              )}
              <div className="flex justify-between" style={{color: "var(--app-muted)"}}>
                <span>+ Biaya Layanan Pembeli ({order.buyer_platform_fee_percent ?? 2}%)</span>
                <span>+ {formatIDR(order.buyer_platform_fee || 0)}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2 font-semibold text-base" style={{borderColor: "var(--app-border)"}}>
                <span>Total Buyer Bayar</span><span>{formatIDR(order.gross_amount)}</span>
              </div>
              <div className="pt-3 mt-2 border-t text-xs space-y-1" style={{color: "var(--app-muted)", borderColor: "var(--app-border)"}}>
                <div className="label-caps mb-1">Sisi Jastiper</div>
                <div className="flex justify-between">
                  <span>Subtotal Item</span>
                  <span>{formatIDR(order.item_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span>− Fee Jastiper ({order.jastiper_platform_fee_percent ?? 2}%)</span>
                  <span>− {formatIDR(order.jastiper_platform_fee || 0)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1 text-sm" style={{color: "var(--app-brand)"}}>
                  <span>Jastiper Terima</span><span>{formatIDR(order.net_to_jastiper)}</span>
                </div>
              </div>
              <div className="text-[10px] pt-2 mt-1 border-t" style={{color: "var(--app-muted)", borderColor: "var(--app-border)"}}>
                Platform Revenue: {formatIDR(order.platform_fee || ((order.buyer_platform_fee||0) + (order.jastiper_platform_fee||0)))}
              </div>
            </div>
          </div>

          {/* Counterparty */}
          <div className="surface p-5">
            <div className="label-caps mb-3">{isBuyer ? "Jastiper Anda" : "Pembeli Anda"}</div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 text-white flex items-center justify-center font-display" style={{backgroundColor: "var(--app-brand)"}}>
                {(isBuyer ? order.jastiper_name : order.buyer_name)?.charAt(0).toUpperCase()}
              </div>
              <div className="font-medium inline-flex items-center gap-1.5">
                {isBuyer ? order.jastiper_name : order.buyer_name}
                {(isBuyer ? order.jastiper_verified_badge : order.buyer_verified_badge) && (
                  <VerifiedBadge size={14} testid="counterparty-verified" />
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="surface p-5 space-y-3" data-testid="order-actions">
            <div className="label-caps mb-1">Aksi</div>

            {/* Buyer: Pay */}
            {isBuyer && order.escrow_status === "pending_payment" && (
              <button onClick={() => handleAction("pay")} disabled={actionLoading} className="btn-primary-flat w-full" data-testid="pay-now-btn">
                <CreditCard size={16} strokeWidth={1.5} /> Bayar Sekarang (MOCK)
              </button>
            )}

            {/* Jastiper: Upload Bukti Pembelian */}
            {isJastiper && order.escrow_status === "paid" && !showReceiptForm && (
              <button onClick={() => setShowReceiptForm(true)} className="btn-primary-flat w-full" data-testid="show-receipt-form-btn">
                <Receipt size={16} strokeWidth={1.5} /> Unggah Bukti Pembelian
              </button>
            )}

            {showReceiptForm && (
              <div className="space-y-3 border-t pt-3" style={{borderColor: "var(--app-border)"}} data-testid="receipt-form">
                <div>
                  <label className="label-caps mb-2 block">Foto Bukti Pembelian</label>
                  <ImageUploader value={receiptPath} onChange={setReceiptPath} testid="receipt-uploader" />
                </div>
                <div>
                  <label className="label-caps mb-2 block">Keterangan (opsional)</label>
                  <input className="input-flat" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="cth. JNE12345678 atau catatan tambahan" data-testid="tracking-input" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleAction("upload-receipt")} disabled={actionLoading} className="btn-primary-flat flex-1" data-testid="submit-receipt-btn">Kirim</button>
                  <button onClick={() => setShowReceiptForm(false)} className="btn-secondary-flat" data-testid="cancel-receipt-btn">Batal</button>
                </div>
              </div>
            )}

            {/* Jastiper: Mark Shipping */}
            {isJastiper && order.escrow_status === "purchased" && (
              <button onClick={() => handleAction("mark-shipping")} disabled={actionLoading} className="btn-primary-flat w-full" data-testid="mark-shipping-btn">
                <Truck size={16} strokeWidth={1.5} /> Tandai Sedang Dikirim
              </button>
            )}

            {/* Buyer: Confirm Arrival */}
            {isBuyer && (order.escrow_status === "purchased" || order.escrow_status === "shipping") && (
              <button onClick={() => handleAction("confirm")} disabled={actionLoading} className="btn-primary-flat w-full" data-testid="confirm-arrival-btn">
                <CheckCircle2 size={16} strokeWidth={1.5} /> Konfirmasi Sampai & Lepas Dana
              </button>
            )}

            {/* Report Issue (buyer & jastiper, when active and not yet disputed) */}
            {!isFinalized && !isDisputed && (isBuyer || isJastiper) && order.escrow_status !== "pending_payment" && !showDisputeForm && (
              <button
                onClick={() => setShowDisputeForm(true)}
                disabled={actionLoading}
                className="w-full text-sm py-2 border-2 inline-flex items-center justify-center gap-2 rounded-md hover:bg-red-50"
                style={{borderColor: "#FECACA", color: "#991B1B"}}
                data-testid="report-issue-btn"
              >
                <AlertTriangle size={14} strokeWidth={1.5} /> Lapor Masalah
              </button>
            )}

            {showDisputeForm && (
              <div className="space-y-3 border-t pt-3" style={{borderColor: "var(--app-border)"}} data-testid="dispute-form">
                <div>
                  <label className="label-caps mb-2 block">Jelaskan Masalahnya</label>
                  <textarea
                    className="input-flat min-h-[100px] text-sm"
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    placeholder="cth. barang tidak sesuai, rusak saat sampai, jastiper tidak responsif..."
                    data-testid="dispute-reason-input"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={reportIssue} disabled={actionLoading} className="btn-primary-flat flex-1" data-testid="submit-dispute-btn">
                    Kirim Laporan
                  </button>
                  <button onClick={() => { setShowDisputeForm(false); setDisputeReason(""); }} className="btn-secondary-flat" data-testid="cancel-dispute-btn">
                    Batal
                  </button>
                </div>
                <p className="text-xs" style={{color: "var(--app-muted)"}}>
                  ⚠ Dana akan ditahan platform sampai admin menyelesaikan sengketa.
                </p>
              </div>
            )}

            {/* Cancel */}
            {!isFinalized && !isDisputed && (
              <button onClick={() => handleAction("cancel")} disabled={actionLoading} className="btn-secondary-flat w-full text-sm" data-testid="cancel-order-btn">
                <XCircle size={14} strokeWidth={1.5} /> Batalkan Order
              </button>
            )}

            {order.escrow_status === "released" && (
              <div className="border p-3 text-sm flex gap-2 items-start" style={{borderColor: "#A7F3D0", backgroundColor: "#D1FAE5", color: "#065F46"}} data-testid="completed-message">
                <CheckCircle2 size={16} strokeWidth={1.5} className="flex-shrink-0 mt-0.5" />
                <span>Order selesai. Dana telah dilepas.</span>
              </div>
            )}
          </div>

          {order.escrow_status === "released" && <ReviewPanel order={order} onSaved={reload} />}
        </div>
      </div>
    </div>
  );
}

function Chat({ orderId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/messages/${orderId}`).then((r) => setMessages(r.data));
  }, [orderId]);

  useEffect(() => {
    const token = localStorage.getItem("jastip_token");
    if (!token) return;
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws/chat/${orderId}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setMessages((prev) => {
          if (prev.find((m) => m.id === data.id)) return prev;
          return [...prev, data];
        });
      } catch {}
    };
    return () => ws.close();
  }, [orderId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ text: t }));
    } else {
      // fallback to HTTP
      try {
        const r = await api.post("/messages", { order_id: orderId, text: t });
        setMessages((prev) => [...prev, r.data]);
      } catch {
        toast.error("Failed to send");
      }
    }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="chat-panel">
      <div className="glass-nav px-5 py-3 flex items-center justify-between border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} strokeWidth={1.5} />
          <div className="label-caps">Live Chat</div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className={`w-2 h-2 rounded-full ${connected ? "bg-green-500 pulse-slow" : "bg-zinc-300"}`} />
          <span className="text-zinc-500 font-mono">{connected ? "LIVE" : "OFFLINE"}</span>
        </div>
      </div>
      <div ref={scrollRef} className="h-64 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="text-center text-zinc-400 text-sm py-12">No messages yet. Say hi 👋</div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                <div className={`max-w-[75%] px-3 py-2 ${mine ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-900"}`}>
                  {!mine && <div className="text-xs font-medium opacity-70 mb-0.5">{m.sender_name}</div>}
                  <div className="text-sm break-words">{m.text}</div>
                  <div className={`text-[10px] font-mono mt-1 ${mine ? "text-white/60" : "text-zinc-400"}`}>{formatDateTime(m.created_at)}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="flex gap-0 border-t border-zinc-200" data-testid="chat-form">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="flex-1 px-4 py-3 bg-transparent outline-none text-sm"
          placeholder="Type a message..."
          data-testid="chat-input"
        />
        <button type="submit" className="px-4 bg-[#002FA7] text-white hover:bg-[#00248a]" data-testid="chat-send-btn">
          <Send size={16} strokeWidth={1.5} />
        </button>
      </form>
    </div>
  );
}

function ReviewPanel({ order, onSaved }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/orders/${order.id}/reviews`).then((r) => setReviews(r.data.reviews || [])).catch(() => {});
  }, [order.id]);

  const isBuyer = user?.id === order.buyer_id;
  const isJastiper = user?.id === order.jastiper_id;
  const myRole = isBuyer ? "buyer" : (isJastiper ? "jastiper" : null);
  const myReview = reviews.find((r) => r.from_user_id === user?.id);
  const otherReview = reviews.find((r) => r.from_user_id !== user?.id);
  const targetName = isBuyer ? order.jastiper_name : order.buyer_name;

  if (!myRole) return null;

  const submit = async () => {
    if (rating < 1) {
      toast.error("Mohon pilih bintang dulu");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/review`, { rating, comment });
      toast.success("Terima kasih atas ratingnya!");
      const r = await api.get(`/orders/${order.id}/reviews`);
      setReviews(r.data.reviews || []);
      onSaved && onSaved();
      setRating(0);
      setComment("");
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal mengirim rating");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-5 space-y-4" data-testid="review-panel">
      <div className="label-caps flex items-center gap-2">
        <Star size={12} strokeWidth={2} style={{color: "#f59e0b", fill: "#f59e0b"}} />
        Rating & Ulasan
      </div>

      {myReview ? (
        <div className="border-l-2 pl-3 py-1" style={{borderColor: "var(--app-brand)"}} data-testid="my-review-summary">
          <div className="text-xs label-caps mb-1">Rating Anda untuk {targetName}</div>
          <StarRating value={myReview.rating} readOnly size={16} testid="my-review-stars" />
          {myReview.comment && <p className="text-sm mt-2 italic">"{myReview.comment}"</p>}
        </div>
      ) : (
        <div className="space-y-3" data-testid="review-form">
          <div>
            <div className="text-sm mb-2">Beri rating untuk {targetName}:</div>
            <StarRating value={rating} onChange={setRating} size={28} testid="rating-input" />
          </div>
          <textarea
            className="input-flat min-h-[80px] text-sm"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tulis ulasan singkat (opsional)"
            data-testid="review-comment"
          />
          <button onClick={submit} disabled={busy || rating < 1} className="btn-primary-flat w-full" data-testid="submit-review-btn">
            <Send size={14} strokeWidth={1.5} />
            {busy ? "Mengirim..." : "Kirim Rating"}
          </button>
        </div>
      )}

      {otherReview && (
        <div className="border-t pt-3 mt-3" style={{borderColor: "var(--app-border)"}} data-testid="other-review">
          <div className="text-xs label-caps mb-1">{otherReview.from_user_name} memberi Anda</div>
          <StarRating value={otherReview.rating} readOnly size={16} testid="other-review-stars" />
          {otherReview.comment && <p className="text-sm mt-2 italic">"{otherReview.comment}"</p>}
        </div>
      )}
    </div>
  );
}

